import { commMemberLabel, type CommAnswer } from '../services/realtime/commClient';
import type { CommResponseBatchRow, PresentationCommunicationResponse } from '../services/presentationCommunicationService';
import type { LessonCommQuestion } from '../hooks/useLessonCommRoom';
import type { ClassMemberRow } from './classMembersStorage';
import {
  COMMUNICATION_OX_CHOICES,
  communicationChoiceOptionLabel,
  evaluateCommunicationQuestionCorrectness,
  type CommunicationQuestion,
} from './presentationCommunicationAnswers';

/**
 * 학생 기기 연결(소통방) ↔ 질문 페이지(DB) 연결 도우미.
 * - 질문 페이지 질문 → 소통방 질문(단답 · OX · 선택형 · 서술형) 변환
 * - 소통방 응답 → 질문 페이지 응답 행(번호 → 학급 멤버 매칭, 정답 여부 계산)
 * - 저장 실패·탭 닫힘 대비 대기열(localStorage)
 */

/** 질문 페이지 질문 → 학생 기기 질문 (서술형은 긴 입력칸·채점 없음) */
export function communicationToLessonCommQuestion(
  id: string,
  promptText: string,
  q: CommunicationQuestion | null
): LessonCommQuestion {
  const text = ((q?.question ?? '').trim() || promptText.trim() || '질문').slice(0, 300);
  if (q?.type === 'ox') return { id, text, questionType: 'ox' };
  if (q?.type === 'essay') return { id, text, questionType: 'essay' };
  if (q?.type === 'choice' && (q.choices?.length ?? 0) > 0) {
    const labels = (q.choices ?? []).map((c, i) => communicationChoiceOptionLabel(c, i));
    if (labels.length === 2 && labels[0].trim().toUpperCase() === 'O' && labels[1].trim().toUpperCase() === 'X') {
      return { id, text, questionType: 'ox' };
    }
    return { id, text, questionType: 'choice', choices: labels.slice(0, 10).map((l) => l.slice(0, 80)) };
  }
  return { id, text, questionType: 'short' };
}

/** 학생 응답 → 질문 페이지 body_text (선택형은 선택지 라벨, 나머지는 입력 그대로) */
export function commAnswerBodyText(a: CommAnswer, q: CommunicationQuestion | null): string {
  const choices = q?.type === 'choice' ? q.choices ?? [] : q?.type === 'ox' ? COMMUNICATION_OX_CHOICES.slice() : [];
  if (choices.length && a.choiceIndex != null && a.choiceIndex >= 0 && a.choiceIndex < choices.length) {
    return communicationChoiceOptionLabel(choices[a.choiceIndex], a.choiceIndex);
  }
  return String(a.content ?? '').trim();
}

/** 번호 → 학급 멤버 (명단 입장). 매칭 안 되면 'comm:<소통방 멤버 id>' 로 저장 */
export function buildCommResponseRows(
  answers: CommAnswer[],
  q: CommunicationQuestion | null,
  members: { no: number; member: ClassMemberRow }[]
): CommResponseBatchRow[] {
  const byNo = new Map(members.map((m) => [m.no, m.member]));
  const out: CommResponseBatchRow[] = [];
  for (const a of answers) {
    const bodyText = commAnswerBodyText(a, q);
    if (!bodyText) continue;
    const m = a.number != null ? byNo.get(a.number) : undefined;
    out.push({
      memberId: m ? m.id : `comm:${a.memberId}`,
      memberName: m ? m.name : commMemberLabel(a),
      /* 명단 입장은 별칭이 없음 → null. 방 입장번호로 들어온 학생만 입력한 이름 보관 */
      nickname: a.number != null ? null : a.nickname || null,
      studentNumber: a.number ?? null,
      bodyText,
      isCorrect: evaluateCommunicationQuestionCorrectness(bodyText, q),
      answeredAtMs: a.at || Date.now(),
    });
  }
  return out;
}

/**
 * 학생 블럭(질문 페이지) 표시용 응답으로 변환 — 명단 번호로 매칭된 학생만.
 * ('comm:' 학생은 블럭이 없어 제외 → 교사 화면 팝업에만 표시)
 */
export function commRowsToResponses(
  rows: CommResponseBatchRow[],
  questionId: string
): PresentationCommunicationResponse[] {
  return rows
    .filter((r) => !r.memberId.startsWith('comm:'))
    .map((r) => ({
      memberId: r.memberId,
      memberNameSnapshot: r.memberName,
      bodyText: r.bodyText,
      updatedAt: new Date(r.answeredAtMs).toISOString(),
      questionId,
    }));
}

/* ---------- 대기열 (저장 실패 / 탭 닫힘) ---------- */
export type PendingCommBatch = {
  presentationRefId: string;
  workspaceId: string;
  questionId: string;
  rows: CommResponseBatchRow[];
  queuedAt: number;
};
const PENDING_KEY = 'cp-comm-pending-responses:v1';
const PENDING_MAX = 20;
const PENDING_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export function loadPendingCommBatches(): PendingCommBatch[] {
  try {
    const raw = window.localStorage.getItem(PENDING_KEY);
    const list = raw ? (JSON.parse(raw) as PendingCommBatch[]) : [];
    const now = Date.now();
    return Array.isArray(list) ? list.filter((b) => b && b.questionId && now - (b.queuedAt || 0) < PENDING_TTL_MS) : [];
  } catch {
    return [];
  }
}
function writePending(list: PendingCommBatch[]) {
  try {
    if (list.length) window.localStorage.setItem(PENDING_KEY, JSON.stringify(list.slice(-PENDING_MAX)));
    else window.localStorage.removeItem(PENDING_KEY);
  } catch {
    /* ignore */
  }
}
/** 같은 질문은 최신 묶음으로 교체 */
export function queuePendingCommBatch(b: PendingCommBatch) {
  writePending([...loadPendingCommBatches().filter((x) => x.questionId !== b.questionId), b]);
}
export function removePendingCommBatch(questionId: string) {
  writePending(loadPendingCommBatches().filter((x) => x.questionId !== questionId));
}
